import React, { useState } from 'react';
import { addInspection } from '../utils/storage';
import { ModalTemplate } from './templates';
import './Modal.css';
import './CreateInspectionModal.css';

// All supported inspection types, grouped by category
const INSPECTION_GROUPS = [
  {
    category: 'Visual Inspection',
    items: [
      { code: 'VT-OSI', label: 'Visual Out of Service Inspection' },
      { code: 'VT-ISI', label: 'Visual In Service Inspection' },
      { code: 'VT',     label: 'Visual Inspection' },
    ],
  },
  {
    category: 'Ultrasonic Inspection',
    items: [
      { code: 'UT-OSI', label: 'Ultrasonic Out of Service Inspection' },
      { code: 'UT-ISI', label: 'Ultrasonic In Service Inspection' },
      { code: 'UT',     label: 'Ultrasonic Inspection' },
    ],
  },
  {
    category: 'Deformation Measurement',
    items: [
      { code: 'DEFO', label: 'Deformation Measurement Analysis' },
    ],
  },
  {
    category: 'Magnetic Inspection',
    items: [
      { code: 'MT-OSI', label: 'Magnetic Out of Service Inspection' },
      { code: 'MT-ISI', label: 'Magnetic In Service Inspection' },
      { code: 'MT',     label: 'Magnetic Inspection' },
    ],
  },
  {
    category: 'Penetrant Inspection',
    items: [
      { code: 'PT-OSI', label: 'Penetrant Out of Service Inspection' },
      { code: 'PT-ISI', label: 'Penetrant In Service Inspection' },
      { code: 'PT',     label: 'Penetrant Inspection' },
    ],
  },
  {
    category: 'Corner-weld Inspection',
    items: [
      { code: 'CW-OSI', label: 'Corner-weld Internal Dimension Inspection' },
    ],
  },
];

// Flat lookup: code → { code, label }
const ALL_TYPES = INSPECTION_GROUPS.flatMap((g) => g.items);

// Returns true if the given code already exists on the object
function isAlreadyAdded(code, existingTypes) {
  if (!existingTypes || existingTypes.length === 0) return false;
  return existingTypes.includes(code);
}

export default function CreateInspectionModal({ objectId, existingTypes = [], objectTag, siteName, onClose, onCreated }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving]     = useState(false);

  function toggle(code) {
    if (isAlreadyAdded(code, existingTypes)) return; // already on object, not toggleable
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function handleAdd() {
    if (selected.size === 0 || saving) return;
    setSaving(true);
    try {
      for (const code of selected) {
        const typeData = ALL_TYPES.find((t) => t.code === code);
        await addInspection(objectId, typeData.label, '', code);
      }
      onCreated?.();
      onClose();
    } catch (err) {
      console.error('Failed to create inspections:', err);
      setSaving(false);
    }
  }

  const count = selected.size;

  return (
    <ModalTemplate
      title="Add Inspection"
      onClose={onClose}
      wide
      className="cim-modal"
      actions={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={count === 0 || saving}
          >
            {saving
              ? 'Adding…'
              : count === 0
                ? 'Select a type'
                : `Add ${count} Inspection${count !== 1 ? 's' : ''}`}
          </button>
        </>
      }
    >
      <p className="cim-hint">
        Select one or more inspection types to add to{' '}
        {objectTag && siteName
          ? <><strong>{objectTag}</strong> of <strong>{siteName}</strong>.</>
          : 'this object.'
        }
      </p>

      <div className="cim-groups">
        {INSPECTION_GROUPS.map((group) => (
          <div key={group.category} className="cim-group">
            <div className="cim-group-label">{group.category}</div>
            <div className="cim-items">
              {group.items.map((item) => {
                const alreadyAdded = isAlreadyAdded(item.code, existingTypes);
                const active       = selected.has(item.code);
                return (
                  <button
                    key={item.code}
                    type="button"
                    className={[
                      'cim-item',
                      alreadyAdded ? 'cim-item-existing' : '',
                      active       ? 'cim-item-active'   : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => toggle(item.code)}
                    disabled={alreadyAdded}
                    title={alreadyAdded ? 'Already added to this object' : undefined}
                  >
                    <span className="cim-item-code">{item.code}</span>
                    <span className="cim-item-label">{item.label}</span>
                    {alreadyAdded
                      ? <span className="cim-item-existing-tick" title="Already added">✓</span>
                      : active && <span className="cim-item-check">✓</span>
                    }
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ModalTemplate>
  );
}
